import React, { useRef, useState, useEffect, useCallback } from "react";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import "./CustomVideoPlayer.css";

export default function CustomVideoPlayer({
  src,
  style = {},
  autoPlay = true,
  loop = true,
  muted = true,
  playsInline = true,
  className = ""
}) {
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);
  
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [isMuted, setIsMuted] = useState(muted);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  
  const controlsTimeoutRef = useRef(null);

  // Keep state in sync with DOM ref on changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = isMuted;

    if (isPlaying) {
      const playPromise = video.play();
      if (playPromise !== undefined) {
        playPromise.catch((error) => {
          console.warn("Autoplay was prevented. Playing muted instead or waiting for user interaction:", error);
          // Auto-play might be blocked by browser if not muted
          if (!isMuted) {
            setIsMuted(true);
            video.muted = true;
            video.play().catch(err => {
              setIsPlaying(false);
            });
          } else {
            setIsPlaying(false);
          }
        });
      }
    } else {
      video.pause();
    }
  }, [isPlaying, isMuted, src]);

  // Handle controls visibility timing
  const resetControlsTimeout = useCallback(() => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying && !isDragging) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 2500);
    }
  }, [isPlaying, isDragging]);

  useEffect(() => {
    resetControlsTimeout();
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [resetControlsTimeout]);

  const handleMouseMoveContainer = () => {
    resetControlsTimeout();
  };

  const handleMouseLeaveContainer = () => {
    if (isPlaying && !isDragging) {
      setShowControls(false);
    }
  };

  const togglePlay = (e) => {
    if (e) e.stopPropagation();
    setIsPlaying((prev) => !prev);
  };

  const toggleMute = (e) => {
    if (e) e.stopPropagation();
    setIsMuted((prev) => !prev);
  };

  const handleTimeUpdate = () => {
    if (videoRef.current && !isDragging) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration || 0);
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      if (video.readyState >= 1) {
        setDuration(video.duration || 0);
      }
    }
  }, [src]);

  // Seek function
  const handleSeek = useCallback((clientX) => {
    if (!videoRef.current || !progressBarRef.current || duration === 0) return;
    const rect = progressBarRef.current.getBoundingClientRect();
    const clickX = clientX - rect.left;
    const width = rect.width;
    const progress = Math.max(0, Math.min(1, clickX / width));
    const newTime = progress * duration;
    
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  const handleMouseDown = (e) => {
    e.stopPropagation();
    setIsDragging(true);
    handleSeek(e.clientX);
  };

  useEffect(() => {
    const handleMouseMoveGlobal = (e) => {
      if (isDragging) {
        handleSeek(e.clientX);
      }
    };

    const handleMouseUpGlobal = () => {
      if (isDragging) {
        setIsDragging(false);
      }
    };

    if (isDragging) {
      window.addEventListener("mousemove", handleMouseMoveGlobal);
      window.addEventListener("mouseup", handleMouseUpGlobal);
    }

    return () => {
      window.removeEventListener("mousemove", handleMouseMoveGlobal);
      window.removeEventListener("mouseup", handleMouseUpGlobal);
    };
  }, [isDragging, handleSeek]);

  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds) || !isFinite(timeInSeconds)) return "00:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div 
      className={`custom-video-player-container ${className}`} 
      style={style}
      onMouseMove={handleMouseMoveContainer}
      onMouseLeave={handleMouseLeaveContainer}
    >
      <video
        ref={videoRef}
        src={src}
        loop={loop}
        playsInline={playsInline}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onClick={togglePlay}
        className="custom-video-element"
      />
      
      <div className={`custom-video-controls ${showControls ? "visible" : ""}`} onClick={(e) => e.stopPropagation()}>
        <button 
          className="control-btn play-pause-btn" 
          onClick={togglePlay}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? (
            <Pause size={16} fill="white" stroke="white" />
          ) : (
            <Play size={16} fill="white" stroke="white" />
          )}
        </button>

        <div 
          className="progress-bar-container"
          ref={progressBarRef}
          onMouseDown={handleMouseDown}
        >
          <div className="progress-bar-bg">
            <div 
              className="progress-bar-fill" 
              style={{ width: `${progressPercent}%` }} 
            />
          </div>
          
          {/* Playhead time tooltip always floating exactly above current progress position */}
          <div 
            className="time-tooltip" 
            style={{ left: `${progressPercent}%` }}
          >
            {formatTime(currentTime)}
          </div>
        </div>

        <button 
          className="control-btn mute-btn" 
          onClick={toggleMute}
          aria-label={isMuted ? "Unmute" : "Mute"}
        >
          {isMuted ? (
            <VolumeX size={18} stroke="white" />
          ) : (
            <Volume2 size={18} stroke="white" />
          )}
        </button>
      </div>
    </div>
  );
}
